import { Dialog } from '@headlessui/react';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import useTranslation from '../../../hooks/useTranslations';
import { feedback, resetFeedback, selectFeedbackError, selectFeedbackStatus } from '../../../redux/modules/authSlice';
import { NegativeSmileyIcon, NeutralSmileyIcon, PositiveSmileyIcon } from '../../misc/icons/Icons';
import Textarea from '../../misc/Textarea';
import ErrorMessage from '../../public/screens/partials/ErrorMessage';
import ModalWrapper from '../layouts/partials/ModalWrapper';

const FeedbackModal = ({ isFeedbackModalOpen, setIsFeedbackModalOpen }) => {
	const t = useTranslation();
	const dispatch = useDispatch();

	const feedbackStatus = useSelector((state) => selectFeedbackStatus(state));
	const feedbackError = useSelector((state) => selectFeedbackError(state));

	const [message, setMessage] = useState('');
	const [rating, setRating] = useState(null);

	const closeModal = () => {
		setIsFeedbackModalOpen(false);
	};

	const handleFeedback = () => {
		if (rating && message) {
			dispatch(feedback({ rating, message }));
		}
	};

	useEffect(() => () => {
		dispatch(resetFeedback());
	}, [dispatch]);

	return (
		<ModalWrapper isModalOpen={isFeedbackModalOpen} setIsModalOpen={setIsFeedbackModalOpen}>
			{feedbackStatus === 'success' && (
				<>
					<Dialog.Title
						as="h3"
						className="text-[1.25rem] font-semibold text-oly-gray-darkest"
					>
						{t('feedback_success_title')}
					</Dialog.Title>
					<div className="flex my-2 gap-x-3">
						<h2>
							{t('feedback_success_message')}
						</h2>
					</div>
					<div className="flex -mx-6 border-t divide-x border-oly-gray-lightest divide-oly-gray-lightest">
						<button
							type="button"
							className="text-left flex-1 h-16 inline-flex justify-center items-center flex-grow px-4 py-2 text-[0.9375rem] font-normal text-oly-blue-dark can-hover:hover:bg-oly-gray-lightestAlpha45 focus-visible:bg-oly-gray-lightestAlpha45 focus:outline-none"
							onClick={closeModal}
						>
							{t('general_close')}
						</button>
					</div>
				</>
			)}
			{(feedbackStatus === null || feedbackStatus === 'error') && (
				<>
					<Dialog.Title
						as="h3"
						className="text-[1.25rem] font-semibold text-oly-gray-darkest"
					>
						{t('feedback_headline')}
					</Dialog.Title>
					<div className="flex my-2 gap-x-3">
						<button
							type="button"
							onClick={() => setRating(3)}
							className={`flex items-center flex-shrink-0 p-2 rounded-lg ${rating === 3 ? 'text-oly-green-default shadow-button' : 'text-oly-gray-darkest can-hover:hover:text-oly-green-default focus-visible:text-oly-green-default can-hover:hover:shadow-button focus-visible:shadow-button'} focus:outline-none`}
							aria-labelledby="positive-rating"
						>
							<PositiveSmileyIcon className="w-8 h-8" />
						</button>
						<button
							type="button"
							onClick={() => setRating(2)}
							className={`flex items-center flex-shrink-0 p-2 rounded-lg ${rating === 2 ? 'text-oly-yellow-default shadow-button' : 'text-oly-gray-darkest can-hover:hover:text-oly-yellow-default focus-visible:text-oly-yellow-default can-hover:hover:shadow-button focus-visible:shadow-button'} focus:outline-none`}
							aria-labelledby="positive-rating"
						>
							<NeutralSmileyIcon className="w-8 h-8" />
						</button>
						<button
							type="button"
							onClick={() => setRating(1)}
							className={`flex items-center flex-shrink-0 p-2 rounded-lg ${rating === 1 ? 'text-oly-red-default shadow-button' : 'text-oly-gray-darkest can-hover:hover:text-oly-red-default focus-visible:text-oly-red-default can-hover:hover:shadow-button focus-visible:shadow-button'} focus:outline-none`}
							aria-labelledby="positive-rating"
						>
							<NegativeSmileyIcon className="w-8 h-8" />
						</button>
					</div>
					<div className="w-full pb-4 mx-auto">
						<Textarea fieldName="feedback" label={t('feedback_subline')} showLabel labelClasses="text-[1.25rem] font-semibold pb-1" placeholder={t('feedback_text_placeholder')} handleChange={setMessage} textareaValue={message} autofocus />
					</div>
					<Dialog.Description
						as="p"
						className="text-[1.0625rem] text-oly-gray-darkest mb-8 leading-6 html-content"
						dangerouslySetInnerHTML={{ __html: t('feedback_text') }}
					/>

					{feedbackError && (
						<div className="mb-2">
							<ErrorMessage message={t(feedbackError)} />
						</div>
					)}

					<div className="flex -mx-6 border-t divide-x border-oly-gray-lightest divide-oly-gray-lightest">
						<button
							type="button"
							className="text-left flex-1 h-16 inline-flex justify-center items-center flex-grow px-4 py-2 text-[0.9375rem] font-normal text-oly-blue-dark can-hover:hover:bg-oly-gray-lightestAlpha45 focus-visible:bg-oly-gray-lightestAlpha45 focus:outline-none"
							onClick={closeModal}
						>
							{t('general_close')}
						</button>
						<button
							type="button"
							className="text-left flex-1 h-16 inline-flex justify-center items-center flex-grow px-4 py-2 text-[0.9375rem] font-semibold text-oly-blue-dark can-hover:hover:bg-oly-gray-lightestAlpha45 focus-visible:bg-oly-gray-lightestAlpha45 focus:outline-none disabled:!bg-white disabled:cursor-not-allowed disabled:opacity-75 disabled:text-oly-gray-normal"
							onClick={() => handleFeedback()}
							disabled={!rating || !message}
						>
							{t('feedback_submit')}
						</button>
					</div>
				</>
			)}
		</ModalWrapper>
	);
};

export default FeedbackModal;
